import { useState, useRef } from 'react';
import axios from 'axios';
import { ImagePlus, X, Loader2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAX_PHOTOS = 5;
const MAX_BYTES = 10 * 1024 * 1024; // 10 MB per photo — HEIC can be big
const ALLOWED = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

/**
 * Reusable photo picker for quote forms. Uploads each selected photo to the
 * backend and calls `onChange` with the accumulated list of QuotePhoto
 * metadata objects. Shows thumbnails with a remove button per photo.
 *
 * @param {Object} props
 * @param {Array} props.value    Current list of uploaded photo metadata objects.
 * @param {Function} props.onChange  Called with the new list after add / remove.
 * @param {string}  [props.variant] Colour variant: "dark" (default, cream cards on green forms) or "light" (green on cream).
 */
export default function PhotoUpload({ value = [], onChange, variant = 'light' }) {
  const { toast } = useToast();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const remaining = Math.max(0, MAX_PHOTOS - value.length);
  const isDark = variant === 'dark';

  const handleFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList).slice(0, remaining);
    if (files.length === 0) {
      toast({ title: `You can add up to ${MAX_PHOTOS} photos.`, variant: 'destructive' });
      return;
    }
    setUploading(true);
    const uploaded = [];
    for (const file of files) {
      if (!ALLOWED.includes(file.type.toLowerCase())) {
        toast({ title: `"${file.name}" isn't a supported photo type.`, description: 'JPG, PNG, WebP or HEIC only.', variant: 'destructive' });
        continue;
      }
      if (file.size > MAX_BYTES) {
        toast({ title: `"${file.name}" is too big (>10MB).`, variant: 'destructive' });
        continue;
      }
      try {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await axios.post(`${API}/quote/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploaded.push({
          ...data,
          previewUrl: URL.createObjectURL(file),
        });
      } catch (err) {
        toast({
          title: `Couldn't upload "${file.name}".`,
          description: err.response?.data?.detail || 'Please try again.',
          variant: 'destructive',
        });
      }
    }
    setUploading(false);
    if (uploaded.length) onChange([...value, ...uploaded]);
    if (inputRef.current) inputRef.current.value = '';
  };

  const removeAt = (idx) => {
    const next = value.slice();
    const [removed] = next.splice(idx, 1);
    if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
    onChange(next);
  };

  return (
    <div data-testid="photo-upload">
      <label className={`block text-[11px] font-bold tracking-widest mb-1 ${isDark ? 'text-white/80' : 'text-gray-600'}`}>
        PHOTOS (OPTIONAL) UP TO {MAX_PHOTOS}
      </label>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid="photo-upload-thumbs">
          {value.map((p, i) => (
            <div key={p.storage_path} className="relative w-20 h-20 rounded-md overflow-hidden border border-[#E2DAC5] bg-[#FAF8F2]">
              <img
                src={p.previewUrl || `${API}/quote/photo/${p.storage_path}`}
                alt={p.filename}
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => removeAt(i)}
                aria-label={`Remove ${p.filename}`}
                className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 hover:bg-black text-white flex items-center justify-center"
                data-testid={`photo-upload-remove-${i}`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading || remaining === 0}
        className={`w-full flex items-center justify-center gap-2 px-3 py-3 rounded-md border-2 border-dashed text-sm font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
          isDark
            ? 'border-white/40 text-white hover:border-[#D98A00] hover:text-[#D98A00] bg-white/5'
            : 'border-[#1B6E37]/40 text-[#0A2E18] hover:border-[#1B6E37] hover:bg-[#1B6E37]/5'
        }`}
        data-testid="photo-upload-btn"
      >
        {uploading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Uploading...
          </>
        ) : (
          <>
            <ImagePlus className="w-4 h-4" />
            {value.length === 0 ? 'Add photos of your lawn or garden' : `Add more (${remaining} left)`}
          </>
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid="photo-upload-input"
      />
      <p className={`text-xs mt-1.5 ${isDark ? 'text-white/60' : 'text-[#4A5447]'}`}>
        JPG, PNG, WebP or HEIC · up to 10MB each. Photos help us quote accurately.
      </p>
    </div>
  );
}
